// How the card's figures and its date are produced. Every trap on this surface lives in this file.
//
// THE RULE BEHIND ALL OF IT (rulings L80-L83): every figure on the Maven card is PER UNIT — ledger
// scale, the net over the portfolio's default quantity. Nothing here multiplies by 100, and nothing
// should be added that does. The raw P&L figure is not on this card at all.
//
// This was got wrong once already, in the design itself: the ledger price was drawn as raw P&L and
// $2,840 reached the owner for a package worth $28.40. If you find yourself reaching for a hundred
// on this surface, that is the mistake reproducing.

import {DateTime} from 'luxon';
import {findHCF, isValidNumber, isVoid} from '../../utils';
import {MavenCard} from './maven-delivery.contract';

/**
 * The card's date line: `Fri 18-Sep-26 (7d)` — the application's standard expiration format,
 * weekday then `dd-MMM-yy`, then days to expiration in PARENTHESES with a `d` suffix.
 *
 * Parentheses, not square brackets: square brackets appear on exactly one surface platform-wide and
 * it is a compressed tooltip.
 *
 * ⚠ TWO AUTHORS FOR ONE NUMBER — the trap this signature exists to avoid (ruling L82). The platform
 * helper `makeFullExpirationDate` (utils.ts:1821) produces this exact string, and at :1839 it
 * computes the days-to-expiration ITSELF through `daysToExpiration`. The delivery already carries
 * one, counted on the backend from Eastern-time today or the Quote Generator cursor. Two authors for
 * one number is a fault this change request has already undone twice — the default quantity, and the
 * nearby-reports window.
 *
 * So the DTE is the caller's, from the delivery, and only the date half is formatted here. The
 * helper has no date-with-weekday-only entry point (`makeGuiFriendlyExpirationDate` drops the
 * weekday, `makeFullExpirationDate` always appends its own DTE), so the date half uses the same
 * single Luxon format line the helper itself uses at :1838.
 */
export function mavenCardDateText(date: string, dte: number): string {

    const datePart = mavenExpirationWithWeekday(date);

    if (datePart === null) {
        return date || '';
    }

    if (!isValidNumber(dte)) {
        return datePart;
    }

    return datePart + ' (' + dte + 'd)';
}

/** `EEE dd-MMM-yy` from the wire's `yyyy-MM-dd`. Null when the date cannot be parsed. */
export function mavenExpirationWithWeekday(date: string): string | null {

    if (isVoid(date)) {
        return null;
    }

    const dt = DateTime.fromFormat(date, 'yyyy-MM-dd');

    return dt.isValid ? dt.toFormat('EEE dd-MMM-yy') : null;
}

/**
 * The Hedges line on the breakdown: the opening hedges with the CLOSE-OUT NETTED IN (ruling L81).
 *
 * Close-out is no longer a line of its own. The breakdown now mirrors the two ORDERS the trader
 * actually sends rather than the three figures the engine happens to produce — and one hedge order
 * already carries both the close and the opening, which is what D4 says.
 *
 * Both terms are signed and in ledger scale, so this is a plain sum: -4.20 close and +13.00 open
 * read as +8.80.
 *
 * Null when the hedges themselves are unpriced. An absent close-out means Ignore — nothing was
 * ordered, so it is a zero term and not a missing one.
 */
export function mavenHedgesLine(hedges: number | null, closeOut: number | null): number | null {

    if (hedges === null || hedges === undefined) {
        return null;
    }

    const close = (closeOut === null || closeOut === undefined) ? 0 : closeOut;

    return hedges + close;
}

/**
 * The ORDER price: the net over the combo's highest common factor (ruling L83).
 *
 * The delivery carries no common factor — there is no such field anywhere on it — and the owner
 * ruled compute over a contract change. So it is derived here from the legs already on the card.
 *
 * The arithmetic, from the two definitions:
 *     ledger = net / defaultQty        =>  net = ledger * defaultQty
 *     order  = net / hcf               =>  order = ledger * defaultQty / hcf
 *
 * Worked, from the approved drawing: ledger 28.40, default quantity 10, common factor 5 gives
 * 56.80. That is the only reason the two totals differ at all — when the legs reduce to the default
 * quantity, they are the same number.
 *
 * Null rather than a guess when the factor cannot be established: a wrong order price is a figure
 * the trader would hand to a broker.
 */
export function mavenOrderPrice(ledgerTotal: number | null, defaultQty: number, hcf: number | null): number | null {

    if (ledgerTotal === null || ledgerTotal === undefined || !isValidNumber(ledgerTotal)) {
        return null;
    }

    if (!isValidNumber(defaultQty, true) || !isValidNumber(hcf, true)) {
        return null;
    }

    return ledgerTotal * defaultQty / hcf;
}

/**
 * The package's highest common factor, over every leg the card carries: the adjustment's legs and
 * the hedge orders' legs.
 *
 * `findHCF` takes absolute values and needs at least one valid quantity; it is the platform's own,
 * called rather than copied. Null when the card carries no quantity to reduce — the caller then
 * shows no order price rather than inventing a factor of one.
 */
export function mavenPackageHcf(card: MavenCard): number | null {

    const quantities: number[] = [];

    const adjustments = card?.adjustment?.adjustments || [];

    adjustments.forEach(adjustment => {
        collectLegQuantities(adjustment, quantities);
    });

    const transactions = card?.hedges?.transaction || [];

    transactions.forEach(transaction => {
        (transaction?.orderTransactions || []).forEach(group => {
            (group?.orders || []).forEach(order => {
                if (isValidNumber(order?.qty, true)) {
                    quantities.push(Math.abs(order.qty));
                }
            });
        });
    });

    if (quantities.length === 0) {
        return null;
    }

    const hcf = findHCF(quantities);

    return isValidNumber(hcf, true) ? hcf : null;
}

function collectLegQuantities(adjustment: any, into: number[]): void {

    if (!adjustment) {
        return;
    }

    const legGroups = [
        adjustment.mainLegs,
        adjustment.linkedLegs,
        adjustment.secondLinkedLegs,
        adjustment.thirdLinkedLegs,
    ];

    legGroups.forEach(legs => {
        (legs || []).forEach((leg: any) => {
            if (leg && isValidNumber(leg.qty, true)) {
                into.push(Math.abs(leg.qty));
            }
        });
    });
}

/**
 * ⚠ PLACEHOLDER SCORE (owner, vrequest 2026-09-13, third pass). No score exists on the delivery yet.
 * Until the backend delivers one, the column shows a stand-in with two properties the owner set:
 * FIXED per card for the session (it survives a re-sort), and ORDERED with the cost per day — a
 * higher cost per day gets a higher score.
 *
 * It is the card's cost per day as a share of the run's best, on a 0-100 scale. Null when the card
 * has no cost per day or the run has no positive best — never a zero, never a guess.
 *
 * DELETE THIS when the delivery carries a score. Nothing else in the panel depends on the formula.
 */
export function mavenPlaceholderScore(perDay: number | null | undefined, bestPerDay: number | null | undefined): number | null {

    if (perDay === null || perDay === undefined || !isValidNumber(perDay)) {
        return null;
    }

    if (bestPerDay === null || bestPerDay === undefined || !isValidNumber(bestPerDay) || bestPerDay <= 0) {
        return null;
    }

    const share = Math.round(100 * perDay / bestPerDay);

    return Math.max(0, Math.min(100, share));
}

/**
 * A signed money figure in LEDGER scale, for display: `$28.40`, `-$4.20`.
 *
 * No multiplication, no scaling, no currency conversion — the figure arrives per unit and is shown
 * per unit. Null renders as an em dash, never as zero and never as a blank (guarantee G4).
 */
export function mavenMoneyText(value: number | null | undefined): string {

    if (value === null || value === undefined || !isValidNumber(value)) {
        return '—';
    }

    const sign = value < 0 ? '-' : '';

    return sign + '$' + Math.abs(value).toFixed(2);
}

/**
 * The adjustment types the package rolls into, joined as the ORION (APG) snippet card joins them:
 * `2B(C) + 2B(P)`, `3(C) + 3(P)`. Shown on the card's Adjustment line AND in the Summary's Pros
 * (owner, 2026-09-21) — one formatter so the two surfaces never disagree.
 *
 * DELIVERED, NEVER DERIVED: the strings are the APG ORION's own `adjustmentTypes`, side suffix
 * included. Nothing here reads the legs. Null when the delivery carries none — the card then shows
 * the bare "Adjustment" label and the Pros row is absent, never a blank or a zero.
 */
export function mavenAdjustmentTypesText(types: string[] | null | undefined): string | null {
    const clean = (types || []).filter(t => !isVoid(t) && t.trim().length > 0);

    return clean.length === 0 ? null : clean.join(' + ');
}
